import { useState, useMemo } from 'react';
import { FileText, Download, CreditCard, AlertCircle, ExternalLink } from 'lucide-react';
import { usePortalInvoices, usePortalDownloadInvoice } from '@/hooks/usePortalInvoices';
import { formatMoney } from '@/hooks/useInvoices';
import type { PortalInvoiceItem } from '@/hooks/usePortalInvoices';

/**
 * Patient portal invoices page.
 * Shows invoices for the patient with payment status and download/pay options.
 */
export function PortalInvoices() {
  const { data: invoices = [], isLoading, error } = usePortalInvoices();
  const [activeTab, setActiveTab] = useState<'all' | 'unpaid' | 'paid'>('all');

  // Filter invoices by tab
  const filteredInvoices = useMemo(() => {
    if (activeTab === 'unpaid') {
      return invoices.filter((inv) => inv.status === 'issued');
    }
    if (activeTab === 'paid') {
      return invoices.filter((inv) => inv.status === 'paid');
    }
    return invoices;
  }, [invoices, activeTab]);

  // Count invoices by status
  const counts = useMemo(() => {
    const unpaid = invoices.filter((inv) => inv.status === 'issued').length;
    const paid = invoices.filter((inv) => inv.status === 'paid').length;
    return { all: invoices.length, unpaid, paid };
  }, [invoices]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
        <div className="flex items-center text-red-800 dark:text-red-200">
          <AlertCircle className="mr-2 h-5 w-5" />
          <span>Failed to load invoices. Please try again.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">My Invoices</h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          View your invoices and payment status
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('all')}
            className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
              activeTab === 'all'
                ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
            }`}
          >
            All ({counts.all})
          </button>
          <button
            onClick={() => setActiveTab('unpaid')}
            className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
              activeTab === 'unpaid'
                ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
            }`}
          >
            Unpaid ({counts.unpaid})
          </button>
          <button
            onClick={() => setActiveTab('paid')}
            className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
              activeTab === 'paid'
                ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
            }`}
          >
            Paid ({counts.paid})
          </button>
        </nav>
      </div>

      {/* Invoices List */}
      {filteredInvoices.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-300 bg-white p-12 text-center dark:border-gray-700 dark:bg-gray-800">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
            No {activeTab === 'all' ? '' : activeTab} invoices
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {activeTab === 'unpaid'
              ? 'You have no unpaid invoices'
              : activeTab === 'paid'
              ? 'You have no paid invoices'
              : 'Your invoices will appear here'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredInvoices.map((invoice) => (
            <InvoiceCard key={invoice.id} invoice={invoice} />
          ))}
        </div>
      )}
    </div>
  );
}

interface InvoiceCardProps {
  invoice: PortalInvoiceItem;
}

function InvoiceCard({ invoice }: InvoiceCardProps) {
  const downloadMutation = usePortalDownloadInvoice();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const url = await downloadMutation.mutateAsync(invoice.id);
      
      // Open the PDF in a new tab
      window.open(url, '_blank');
    } catch (err) {
      console.error('Failed to download invoice:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  const statusColors = {
    draft: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    issued: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    paid: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    overdue: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    void: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  };

  // Check if overdue
  const isOverdue = 
    invoice.status === 'issued' && 
    invoice.due_at && 
    new Date(invoice.due_at) < new Date();

  const displayStatus = isOverdue ? 'overdue' : invoice.status;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {/* Invoice Number & Clinic */}
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {invoice.invoice_number || `Invoice #${invoice.id.slice(0, 8)}`}
              </h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {invoice.clinic_name}
              </p>
            </div>
            
            {/* Status Badge */}
            <span
              className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                statusColors[displayStatus as keyof typeof statusColors] ?? statusColors.draft
              }`}
            >
              {displayStatus.replace('_', ' ').toUpperCase()}
            </span>
          </div>

          {/* Invoice Details */}
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Total Amount:</span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {formatMoney(invoice.total_cents, invoice.currency)}
              </span>
            </div>
            
            {invoice.issued_at && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Issued:</span>
                <span className="text-gray-900 dark:text-white">
                  {formatDate(invoice.issued_at)}
                </span>
              </div>
            )}
            
            {invoice.due_at && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Due:</span>
                <span className={`font-medium ${isOverdue ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                  {formatDate(invoice.due_at)}
                </span>
              </div>
            )}
            
            {invoice.paid_at && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Paid:</span>
                <span className="text-green-600 dark:text-green-400">
                  {formatDate(invoice.paid_at)}
                </span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-4 flex items-center justify-end space-x-3 border-t border-gray-200 pt-4 dark:border-gray-700">
            {/* Download PDF */}
            <button
              onClick={handleDownload}
              disabled={isDownloading}
              className="flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            >
              {isDownloading ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-gray-700 dark:border-gray-300" />
                  Loading...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Download PDF
                </>
              )}
            </button>

            {/* Pay Now Button (if unpaid and has payment link) */}
            {invoice.status === 'issued' && invoice.pay_url && (
              <a
                href={invoice.pay_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700"
              >
                <CreditCard className="mr-2 h-4 w-4" />
                Pay Now
                <ExternalLink className="ml-2 h-3 w-3" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
